import * as React from 'react'
// libs
import { reset } from 'redux-form'
import { useDispatch, useSelector } from 'react-redux'
// components
import { Header } from 'components/Modals/PlanItems/styles'
import { DrawerModal } from 'components/UI'
// view components
import PickListOptionModal from '../PickListOptionModal'
import PropertyDetailsForm from 'components/Modals/PropertyDetailsModal/components/Form'
// hooks
import { useModalData } from 'hooks/index'
// context
import { RiskTemplateAPIContext } from 'containers/Risks/RiskTemplate/Edit/layers/API'
// redux
import { responseTypesSelector, riskTemplatesSelector } from 'store/reducers/riskTemplates'
// constants
import { USER_DETAILS_FIELDS_OPTIONS } from 'components/Modals/PropertyDetailsModal/components/Form/components/LookupSettings'
import { DATA_SOURCE_OPTIONS, ENTITIES, Forms, Modals } from 'constants/index'
// themes
import { CloseIcon } from 'components/UI/Icons'
// types
import { FieldType, IPlanType, IField, IObjectEntity } from 'types/graphql'
// utils
import { get, startCase } from 'lodash'
import { getSettingsByType } from './utils'
import { getOptionByValue, getOptionsByValue } from 'utils/form'

interface Props {
  object: IObjectEntity
  updateField: any
  selectedPlanType?: IPlanType
}

const PropertyDetailsDrawer: React.FC<Props> = ({ object, updateField, selectedPlanType }) => {
  const dispatch = useDispatch()

  const { basicFields } = React.useContext(RiskTemplateAPIContext)

  const riskTemplates = useSelector(riskTemplatesSelector)
  const responseTypeSets = useSelector(responseTypesSelector)

  const {
    hideModal,
    modalData: { isOpen, isPlanType, property }
  } = useModalData<{ isPlanType: boolean; property: IField }>({ modalType: Modals.PROPERTY_DETAILS_MODAL })

  if (!property) {
    return null
  }

  // constants
  const objectName = object.name.toLowerCase()
  const propertyGrouping = isPlanType ? `About this ${selectedPlanType.description}` : `About this ${objectName}`
  const parentField = basicFields?.find((f) => f.fieldType === FieldType.Section && f.id === property.parentFieldId)
  const source = property.source?.map((source) => getOptionByValue(source, DATA_SOURCE_OPTIONS)) || []
  const isReviewField = object.reviewFieldIds?.includes(property.id)

  const initialValues = {
    fieldId: property.id,
    active: property.active,
    fieldType: startCase(property.fieldType.toLowerCase()),
    format: Array.isArray(property.format) ? property.format[0] : property.format,
    grouping: propertyGrouping,
    multiline: !!property.multiline,
    multiple: !!property.multiple,
    name: property.label,
    object: object.name,
    hidden: property.hidden,
    options: property.options || [],
    required: property.required,
    historyEnabled: property.historyEnabled,
    displayEmail: property.displayEmail,
    backgroundColor: property.backgroundColor,
    source: property.searchTemplateIds
      ? [
          ...source,
          ...property.searchTemplateIds.map((id) => {
            const label = riskTemplates.find((item) => item.id === id)?.name

            return { label, value: id, entity: ENTITIES.RISK }
          })
        ]
      : source,
    globalPropertyName: get(property, 'parentField.label'),
    wysiwyg: property.wysiwyg,
    searchable: property.searchable,
    readonly: property.readonly,
    restricted: property.restricted,
    responseType: property.responseType,
    hideName: property.hideName,
    hideOnPublic: property.hideOnPublic,
    isPublicOnly: property.isPublicOnly,
    includeInAiContext: property.includeInAiContext,
    aiAutoPopulate: property.aiAutoPopulate,
    aiIncludeHistory: property.aiIncludeHistory,
    aiContextDescription: property.aiContextDescription,
    aiSuggestedPrompts: property.aiSuggestedPrompts,
    showUserDetails: property.showUserDetails,
    visibleUserDetailsFields: getOptionsByValue(property.visibleUserDetailsFields, USER_DETAILS_FIELDS_OPTIONS),
    exportHeaders: property.exportHeaders,
    includeHistoryInApiResponse: property.includeHistoryInApiResponse,
    weight: property.weight?.toString(),
    responseTypeId: getOptionByValue(
      property.responseTypeId,
      responseTypeSets.map((set) => ({
        value: set.id,
        label: set.options.map(({ label }) => label).join(', ')
      }))
    ),
    parentFieldId: parentField ? { label: parentField.label, value: parentField.id } : null,
    ...(get(property, 'objectSecurity') && {
      objectSecurity: {
        value: property.objectSecurityId,
        label: property.objectSecurity.name
      }
    }),
    settings: getSettingsByType(property, riskTemplates)
  }

  const handleFormSubmit = async (data: Record<string, any>) => {
    await updateField(data, property, initialValues)

    hideModal()

    dispatch(reset(Forms.PROPERTY_EDIT_FORM))
  }

  return (
    <DrawerModal width='500px' isOpen={isOpen} hideModal={hideModal} data-testid='property-details-drawer'>
      <Header>
        Property Information
        <CloseIcon onClick={hideModal} data-testid='close-property-details-drawer' />
      </Header>
      <PropertyDetailsForm
        property={property}
        onSubmit={handleFormSubmit}
        initialValues={initialValues}
        isReviewField={isReviewField}
      />
      <PickListOptionModal />
    </DrawerModal>
  )
}

export default PropertyDetailsDrawer
